import { createSlice, PayloadAction } from '@reduxjs/toolkit'

interface UIState {
  loading: {
    formData: boolean
    auth: boolean
    general: boolean
  }
  errors: {
    formData: string | null
    auth: string | null
    general: string | null
  }
  modals: {
    isOpen: boolean
    type: string | null
    data: unknown
  }
  notifications: Array<{
    id: string
    type: 'success' | 'error' | 'warning' | 'info'
    message: string
    timestamp: number
  }>
}

const initialState: UIState = {
  loading: {
    formData: false,
    auth: false,
    general: false,
  },
  errors: {
    formData: null,
    auth: null,
    general: null,
  },
  modals: {
    isOpen: false,
    type: null,
    data: null,
  },
  notifications: [],
}

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // Loading states
    setLoading: (state, action: PayloadAction<{ type: keyof UIState['loading']; loading: boolean }>) => {
      const { type, loading } = action.payload
      state.loading[type] = loading
    },

    // Error states
    setError: (state, action: PayloadAction<{ type: keyof UIState['errors']; error: string | null }>) => {
      const { type, error } = action.payload
      state.errors[type] = error
    },
    clearErrors: (state) => {
      state.errors = {
        formData: null,
        auth: null,
        general: null,
      }
    },

    // Modal management
    openModal: (state, action: PayloadAction<{ type: string; data?: unknown }>) => {
      state.modals = {
        isOpen: true,
        type: action.payload.type,
        data: action.payload.data || null,
      }
    },
    closeModal: (state) => {
      state.modals = {
        isOpen: false,
        type: null,
        data: null,
      }
    },

    // Notification management
    addNotification: (
      state,
      action: PayloadAction<{
        type: 'success' | 'error' | 'warning' | 'info'
        message: string
      }>
    ) => {
      const notification = {
        id: Date.now().toString(),
        type: action.payload.type,
        message: action.payload.message,
        timestamp: Date.now(),
      }
      state.notifications.push(notification)
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter((n) => n.id !== action.payload)
    },
    clearNotifications: (state) => {
      state.notifications = []
    },
  },
})

export const {
  setLoading,
  setError,
  clearErrors,
  openModal,
  closeModal,
  addNotification,
  removeNotification,
  clearNotifications,
} = uiSlice.actions

export default uiSlice.reducer
